/**
 * /ko/comparisons/notion-ai — Draftlize vs Notion (Korean). Links to
 * /ko/decision-log.
 */
import { MarketingShell } from '../MarketingShell';

export function NotionAiKo() {
  return (
    <MarketingShell
      currentLocale="ko"
      hreflangPath="/comparisons/notion-ai"
      hreflangLocales={['ko']}
      documentTitle="제품 업무를 위한 Notion 대안｜Draftlize vs Notion · Draftlize"
      documentDescription="Notion은 페이지를 저장합니다. Draftlize는 AI 에이전트가 읽고 쓰는 카드 그래프로 결정을 최신으로 유지합니다. 나란히 비교."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">비교 · vs Notion</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.4vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            Draftlize vs Notion——<br />
            <em>제품 업무엔 어느 쪽?</em>
          </h1>
          <p className="lede">
            결론부터: Notion은 무언가를 적어 두는 데 최고의 도구입니다. Draftlize는 AI 에이전트가 제품 사고를 구조화된 카드로 <em>읽고, 또 쓸 수 있는</em> 기반입니다. 둘은 인접한 문제를 풉니다——하나로 다른 하나를 대체한다기보다, 지금 필요한 게 어느 쪽인지 가리는 문제입니다.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">요점</span>
            <h2 className="serif">나란히 비교, <em>7가지 관점.</em></h2>
          </div>
          <p>같은 로드맵·같은 시나리오를 두 도구에서 각각 돌려 본 결과입니다.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Notion</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>기반의 형태</td><td className="ours yes">타입이 있는 카드 그래프</td><td className="partial">블록으로 된 페이지</td></tr>
            <tr><td>AI가 결정을 매번 컨텍스트로 읽음</td><td className="ours yes">네이티브</td><td className="no">—</td></tr>
            <tr><td>AI가 구조화된 카드로 되쓰기</td><td className="ours yes">네이티브</td><td className="partial">블록은 쓰지만 구조 없음</td></tr>
            <tr><td>의존이 바뀌면 stale 전파</td><td className="ours yes">네이티브</td><td className="no">—</td></tr>
            <tr><td>자유 형식 문서・DB</td><td className="no">—</td><td className="yes">우수</td></tr>
            <tr><td>팀 위키・권한・협업</td><td className="no">개인 우선(v1)</td><td className="yes">우수</td></tr>
            <tr><td>Claude Code / MCP 연동</td><td className="ours yes">네이티브</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif"><em>Notion이 이기는</em> 지점.</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">위키가 필요하다면</h3>
            <p>Notion은 현존 최고의 위키 도구입니다. "팀 운영 매뉴얼을 어디에 둘까"가 문제라면 Notion. Draftlize는 위키를 지향하지 않습니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">표와 DB에서 산다면</h3>
            <p>Notion의 데이터베이스·뷰 시스템은 압도적입니다. 로드맵 트래커, 스프린트 그리드, 평가 표——이런 형태는 Notion의 구조화 컬렉션이 늘 앞섭니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">팀 규모가 크다면</h3>
            <p>실시간 협업, 세밀한 권한, 코멘트, 멘션——Notion은 이를 위해 만들어졌습니다. Draftlize는 개인 우선 설계입니다.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif"><em>Draftlize가 이기는</em> 지점.</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">정말 "기억하는" AI</h3>
            <p>Notion AI의 기억은 대화로의 최선 검색입니다. Draftlize에서는 에이전트가 일하는 기반 자체가 카드라서, 가격 결정 카드를 매 대화마다 자동으로 읽습니다. 재설명이 0으로.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">spec drift가 보이게 된다</h3>
            <p>Notion 페이지를 바꿔도 아무도 모릅니다. Draftlize의 결정 카드를 바꾸면 의존하는 카드가 자동으로 <code>stale</code>. 빌드 시스템이 무효화를 추적하듯 기반이 변경을 추적합니다.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Claude Code / Cursor / MCP를 위한 일급 컨텍스트</h3>
            <p>Notion AI는 Notion 안에만 있습니다. Draftlize 카드는 CLI로 repo에 내려가고 MCP로 각 AI 도구에서 참조됩니다. 결정은 어느 도구에서든 가리킬 수 있습니다. 이어서 <a href="/ko/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>의사결정 기록</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Notion은 "팀이 어디에 적어 두는가"의 답. Draftlize는 "AI가 일의 절반을 할 때 제품 사고가 어디에 쌓이는가"의 답.
        </blockquote>
        <cite>앞의 답만 필요하다면, Notion으로 충분합니다.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">$5 무료로 <em>시작.</em></h2>
        </div>
        <div className="trust-strip">
          <span>신규 가입 시 $5 지급</span>
          <span>사용한 만큼만 결제</span>
          <span>잔액은 만료되지 않음</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Notion은 그대로 써도 됩니다. Draftlize는 AI가 관여하는 부분 아래에 까는 기반입니다. 신규 가입 시 $5, 사용한 만큼만, 잔액은 만료되지 않음.
          </p>
          <a className="btn-primary" href="/">$5 무료로 시작하기</a>
        </div>
      </section>
    </MarketingShell>
  );
}
